import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import StarRating from "../../components/StarRating";
import { SettingsData } from "../../lib/languages";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../lib/api";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Avatar, DatePicker, Image } from "antd";
import { cn, DateFormate } from "../../lib/utils";
import dayjs from "dayjs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Edit3, Mail, Lock, Eye, EyeOff, Loader2, Star } from "lucide-react";
import { Skeleton } from "src/components/ui/skeleton";
import { title } from "motion/react-client";
import UploadImageProfile from "../../components/common/UploadImageProfile";
import { motion } from "framer-motion";
import RateButton from "./../../components/common/RateButton";
import { RatingDialog } from "../../components/common/RatingDialog";

export default function UserSettings() {
  const { language } = useLanguage();
  const t = SettingsData[language] || SettingsData.en;

  const location = useLocation();
  const userData = location.state?.userData;

  const { user: currentUser } = useAuth();

  const user = userData ? userData : currentUser;

  const [profileData, setProfileData] = useState(null);
  const [reviews, setReviews] = useState([]);

  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [saving, setSaving] = useState(false);

  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [personalInfoDialogOpen, setPersonalInfoDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const fetchProfile = async () => {
    setLoadingProfile(true);
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/student-profile/${user.id}`
      );
      setProfileData(data.data);
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string")
        toast(t.error, { description: msg, status: "error" });
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchReviews = async () => {
    setLoadingReviews(true);
    try {
      const { data } = await axios.get(`${BaseUrlApi}/reviews/user/${user.id}`);
      setReviews(data.data);
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string")
        toast(t.error, { description: msg, status: "error" });
    } finally {
      setLoadingReviews(false);
    }
  };

  const [loadingCourses, setLoadingCourses] = useState(true);
  const [courses, setCourses] = useState([]);

  const getAllCourses = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/student/courses`);
      setCourses(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    if (user.id) {
      fetchProfile();
      fetchReviews();
      getAllCourses();
    }
  }, []);

  const handleSaveProfile = async (updated) => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${BaseUrlApi}/student-profile/${user.id}`,
        updated
      );
      setProfileData(data.data);
      toast(t.saveSuccess || "Profile saved", { status: "success" });
      setProfileDialogOpen(false);
      setPersonalInfoDialogOpen(false);
      setPasswordDialogOpen(false);
      setEmailDialogOpen(false);
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string")
        toast(t.error, { description: msg, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const SkeletonCard = ({ height = "h-16" }) => (
    <Skeleton className={`${height} w-full`} />
  );

  const isSameUser = currentUser?.id === user?.id;
  const isAdmin = currentUser?.role === "admin";

  if (loadingProfile || !profileData) {
    return (
      <div className="space-y-6">
        <p className="font-semibold text-xl">{t.myProfile}</p>
        <SkeletonCard height="h-28" />
        <div className="flex items-center gap-6">
          <SkeletonCard height="h-28" />
          <SkeletonCard height="h-28" />
        </div>
        <p className="font-semibold text-lg">{t.personalInfo}</p>
        <SkeletonCard height="h-52" />
      </div>
    );
  }

  // timezone and current time
  const userTimeZone =
    profileData.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentTime = new Date().toLocaleTimeString("en-US", {
    timeZone: userTimeZone,
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="space-y-6" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Title and save */}
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-xl">{t.myProfile}</p>
      </div>
      {/* Profile Card */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            {/* Avatar + info */}
            <div className="flex items-center gap-3">
              {profileData.user_id.image ? (
                <Image
                  // className="z-50"
                  wrapperStyle={{
                    width: "5rem",
                    height: "5rem",
                    borderRadius: "9999px",
                    overflow: "hidden",
                    border: "1px solid hsl(var(--border))",
                  }}
                  src={`${UrlImage}${profileData.user_id.image}`}
                />
              ) : (
                <Avatar
                  src={`${UrlImage}${profileData.user_id.image}`}
                  className={cn(
                    "w-20 h-20 text-lg rounded-full flex items-center justify-center font-medium",
                    "bg-blue-100 text-blue-600",
                    "dark:bg-blue-900 dark:text-blue-300"
                  )}
                >
                  {user?.name.substring(0, 2).toUpperCase()}
                </Avatar>
              )}

              <div className="-space-y-1">
                <p className="font-semibold truncate text-xl mb-1">
                  {profileData.user_id.name}
                </p>
                <p className="text-muted-foreground truncate">
                  {profileData.user_id.user_name}
                </p>
                <p className="text-muted-foreground truncate">
                  {profileData.country || t.notSpecified}
                </p>
              </div>
            </div>
            {isSameUser || isAdmin ? (
              <Button
                variant="outline"
                onClick={() => setProfileDialogOpen(true)}
              >
                {t.edit}
                <Edit3 className="ml-2 h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
      {/* Email & Password Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Email Card */}
        <Card className="overflow-hidden transition-all duration-300 hover:shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">
              <Mail className="mr-2 h-5 w-5 text-blue-500" />
              {t.email || "Email"}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="flex items-center justify-between">
              <p className="font-medium">{profileData.user_id.email}</p>
              {isSameUser || isAdmin ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEmailDialogOpen(true)}
                  className="rounded-full"
                >
                  {t.change || "Change"}
                  <Edit3 className="ml-2 h-3 w-3" />
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Password Card */}
        <Card className="overflow-hidden transition-all duration-300 hover:shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">
              <Lock className="mr-2 h-5 w-5 text-blue-500" />
              {t.password || "Password"}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="flex items-center justify-between">
              <p className="font-medium">••••••••</p>
              {isSameUser || isAdmin ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPasswordDialogOpen(true)}
                  className="rounded-full"
                >
                  {t.change || "Change"}
                  <Edit3 className="ml-2 h-3 w-3" />
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Personal information */}
      <p className="font-semibold text-lg">{t.personalInfo}</p>
      <Card>
        <CardContent className="p-4 space-y-4 relative">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-muted-foreground text-xs">
                {t.educationalStage}
              </p>
              <p className="font-semibold text-sm">
                {profileData.study_level || "Not specified"}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{t.institute}</p>
              <p className="font-semibold text-sm">
                {profileData.institute || "Not specified"}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{t.number}</p>
              <p className="font-semibold text-sm">
                {profileData.user_id.phone || "Not specified"}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{t.currentTime}</p>
              <p className="font-semibold text-sm">
                {currentTime} ({userTimeZone})
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{"Gender"}</p>
              <p className="font-semibold text-sm">
                {profileData.gender || "No gender provided."}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{"Birthday"}</p>
              <p className="font-semibold text-sm">
                {profileData.birthday || "No birthday provided."}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{"Address"}</p>
              <p className="font-semibold text-sm">
                {profileData.address || "No address provided."}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-muted-foreground text-xs">{t.aboutMe}</p>
              <p className="font-semibold text-sm">
                {profileData.description || "No description provided."}
              </p>
            </div>
            <div className="col-span-2 space-y-2">
              <p className="text-muted-foreground text-xs">{t.subjects}</p>
              <div className="flex items-center gap-2 flex-wrap">
                {loadingCourses ? (
                  <>
                    <Skeleton className="rounded-full w-24 h-8" />
                    <Skeleton className="rounded-full w-24 h-8" />
                    <Skeleton className="rounded-full w-24 h-8" />
                  </>
                ) : (
                  courses.map((item, index) => (
                    <Button
                      className="rounded-full bg-blue-600 text-white hover:bg-blue-600/80"
                      size="sm"
                    >
                      {item.title[language] || item.title.en || item.title.ar}
                    </Button>
                  ))
                )}
              </div>
            </div>
          </div>
          <div
            className={`absolute top-0 ${
              language === "ar" ? "left-4" : "right-4"
            }`}
          >
            {isSameUser || isAdmin ? (
              <Button
                className="rounded-full px-5"
                variant="outline"
                onClick={() => setPersonalInfoDialogOpen(true)}
              >
                {t.edit}
                <Edit3 className="ml-2 h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
      {/* Reviews */}
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-lg">{t.reviews}</p>
      </div>
      {loadingReviews ? (
        <p>{t.loadingReviews || "Loading reviews..."}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reviews.length > 0 ? (
            reviews.map((r) => <ReviewCard key={r.id} review={r} />)
          ) : (
            <p className="text-muted-foreground col-span-2">
              {t.noReviews || "No reviews yet."}
            </p>
          )}
        </div>
      )}
      {/* Edit Profile Dialog */}
      <EditProfileDialog
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
        profileData={profileData}
        onSave={handleSaveProfile}
        saving={saving}
        setSaving={setSaving}
      />
      {/* Edit Personal Info Dialog */}
      <EditPersonalInfoDialog
        open={personalInfoDialogOpen}
        onOpenChange={setPersonalInfoDialogOpen}
        profileData={profileData}
        onSave={handleSaveProfile}
        saving={saving}
      />
      {/* Change Email Dialog */}
      <ChangeEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        currentEmail={profileData.user_id.email}
        saving={saving}
        onSave={handleSaveProfile}
      />
      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onOpenChange={setPasswordDialogOpen}
        saving={saving}
        onSave={handleSaveProfile}
      />
      {!isSameUser && <RateButton profileData={profileData} />}
    </div>
  );
}

const ReviewCard = ({ review }) => {
  return (
    <Card className="overflow-hidden h-fit transition-transform duration-500 hover:scale-[1.01]">
      <CardContent className="p-4 space-y-1">
        {/* <p className="font-semibold text-lg mb-2">
          {review.course?.title || "Review"}
        </p> */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-muted-foreground text-base">{review.message}</p>
          <StarRating rating={Number.parseInt(review.rate)} />
        </div>
        <div className="flex items-center justify-between gap-2 text-muted-foreground text-sm">
          <p className="">{DateFormate(review.created_at)}</p>
          <p className="">{review.reviewer?.name || "Anonymous"}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export function EditProfileDialog({
  open,
  onOpenChange,
  profileData,
  onSave,
  saving,
  setSaving,
}) {
  const { language } = useLanguage();
  const t = SettingsData[language] || SettingsData["en"];

  const [formData, setFormData] = useState({
    user_id: {
      name: profileData.user_id.name,
      user_name: profileData.user_id.user_name,
      image: profileData.user_id.image,
    },
    country: profileData.country,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[425px]"
        dir={language === "en" ? "ltr" : "rtl"}
      >
        <DialogHeader>
          <DialogTitle>{t.editProfile || "Edit Profile"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="flex items-center justify-center">
              <UploadImageProfile
                name="users"
                disabled={false}
                imageUrl={
                  formData.user_id.image &&
                  `${UrlImage}${formData.user_id.image}`
                }
                onImageUploaded={(url) =>
                  setFormData({ ...formData, user_id: { image: url } })
                }
                isUploading={(uploading) => setSaving(uploading)}
              />
            </div>

            <div className="grid gap-2">
              <label htmlFor="name">{t.name || "Name"}</label>
              <Input
                id="name"
                name="user_id.name"
                value={formData.user_id.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="username">{t.username || "Username"}</label>
              <Input
                id="username"
                name="user_id.user_name"
                value={formData.user_id.user_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="country">{t.country || "Country"}</label>
              <Input
                id="country"
                name="country"
                value={formData.country || ""}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t.save || "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditPersonalInfoDialog({
  open,
  onOpenChange,
  profileData,
  onSave,
  saving,
}) {
  const { language } = useLanguage();
  const t = SettingsData[language] || SettingsData["en"];

  const [formData, setFormData] = useState({
    user_id: {
      phone: profileData.user_id.phone || "",
    },
    study_level: profileData.study_level || "",
    institute: profileData.institute || "",
    description: profileData.description || "",
    gender: profileData.gender || "",
    birthday: profileData.birthday || null,
    address: profileData.address || "",
    time_zone:
      profileData.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  useEffect(() => {
    setFormData({
      ...formData,
      birthday: profileData.birthday || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData.birthday]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleDateChange = (date, dateString) => {
    setFormData({
      ...formData,
      birthday: dateString,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[525px] max-h-[90vh] overflow-auto"
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t.editPersonalInfo || "Edit Personal Information"}
          </DialogTitle>
          <DialogDescription>
            {t.editPersonalInfoDesc ||
              "Update your personal details and preferences"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              {/* Educational Stage */}
              <div className="space-y-2">
                <label htmlFor="study_level" className="text-sm font-medium">
                  {t.educationalStage || "Educational Stage"}
                </label>
                <Input
                  id="study_level"
                  name="study_level"
                  value={formData.study_level}
                  onChange={handleChange}
                  className="w-full"
                  placeholder={t.educationalStage || "Educational Stage"}
                />
              </div>

              {/* Institute */}
              <div className="space-y-2">
                <label htmlFor="institute" className="text-sm font-medium">
                  {t.institute || "Institute"}
                </label>
                <Input
                  id="institute"
                  name="institute"
                  value={formData.institute}
                  onChange={handleChange}
                  className="w-full"
                  placeholder={t.institute || "Institute"}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Phone */}
              <div className="space-y-2">
                <label htmlFor="phone" className="text-sm font-medium">
                  {t.phone || "Phone"}
                </label>
                <Input
                  id="phone"
                  type="number"
                  name="user_id.phone"
                  value={formData.user_id.phone}
                  onChange={handleChange}
                  placeholder="123-456-7890"
                />
              </div>
              {/* Time Zone */}
              <div className="space-y-2">
                <label htmlFor="time_zone" className="text-sm font-medium">
                  {t.timeZone || "Time Zone"}
                </label>
                <Input
                  id="time_zone"
                  name="time_zone"
                  value={formData.time_zone}
                  onChange={handleChange}
                  placeholder="UTC+2"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Gender */}
              <div className="space-y-2">
                <label htmlFor="gender" className="text-sm font-medium">
                  {t.gender || "Gender"}
                </label>
                <Select
                  value={formData.gender || ""}
                  onValueChange={(value) =>
                    handleChange({ target: { name: "gender", value } })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={t.selectGender || "Select gender"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t.male || "Male"}</SelectItem>
                    <SelectItem value="female">
                      {t.female || "Female"}
                    </SelectItem>
                    <SelectItem value="other">{t.other || "Other"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Birthday with styled DatePicker */}
              <div className="space-y-2">
                <label htmlFor="birthday" className="text-sm font-medium">
                  {t.birthday || "Birthday"}
                </label>
                <div className="datepicker-wrapper">
                  <DatePicker
                    id="birthday"
                    value={formData.birthday ? dayjs(formData.birthday) : null}
                    onChange={handleDateChange}
                    format="YYYY-MM-DD"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    placeholder={t.selectDate || "Select date"}
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <label htmlFor="address" className="text-sm font-medium">
                {t.address || "Address"}
              </label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder={t.addressPlaceholder || "Your address"}
              />
            </div>

            {/* About Me */}
            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">
                {t.aboutMe || "About Me"}
              </label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder={
                  t.aboutMePlaceholder || "Tell us about yourself..."
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t.save || "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ChangeEmailDialog({
  open,
  onOpenChange,
  currentEmail,
  saving,
  onSave,
}) {
  const { language } = useLanguage();
  const t = SettingsData[language] || SettingsData.en;

  const [formData, setFormData] = useState({
    user_id: { currentPassword: "", newEmail: "" },
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.user_id.currentPassword) {
      newErrors.currentPassword = t.passwordRequired || "Password is required";
    }

    if (!formData.user_id.newEmail) {
      newErrors.newEmail = t.emailRequired || "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.user_id.newEmail)) {
      newErrors.newEmail = t.invalidEmail || "Invalid email format";
    } else if (formData.user_id.newEmail === currentEmail) {
      newErrors.newEmail =
        t.sameEmail || "New email must be different from current email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BaseUrlApi}/check-password`, {
        password: formData.user_id.currentPassword,
      });
      onSave({
        user_id: {
          password: formData.user_id.currentPassword,
          email: formData.user_id.newEmail,
        },
      });
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string") {
        toast(t.error, { description: msg, status: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[425px]"
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        <DialogHeader>
          <DialogTitle>{t.changeEmail || "Change Email"}</DialogTitle>
          <DialogDescription>
            {t.changeEmailDesc || "You need to verify your password first"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="currentPassword">
                {t.currentPassword || "Current Password"}
              </label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  name="user_id.currentPassword"
                  type="password"
                  value={formData.user_id.currentPassword}
                  onChange={handleChange}
                  className={errors.currentPassword ? "border-red-500" : ""}
                />
              </div>
              {errors.currentPassword && (
                <p className="text-red-500 text-sm">{errors.currentPassword}</p>
              )}
            </div>

            <div className="grid gap-2">
              <label htmlFor="newEmail">{t.newEmail || "New Email"}</label>
              <Input
                id="newEmail"
                name="user_id.newEmail"
                type="email"
                value={formData.user_id.newEmail}
                onChange={handleChange}
                className={errors.newEmail ? "border-red-500" : ""}
              />
              {errors.newEmail && (
                <p className="text-red-500 text-sm">{errors.newEmail}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || loading}>
              {(saving || loading) && (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              )}
              {t.changeEmail || "Change Email"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ChangePasswordDialog({ open, onOpenChange, saving, onSave }) {
  const { language } = useLanguage();
  const t = SettingsData[language] || SettingsData.en;

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };
  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword =
        t.currentPasswordRequired || "Current password is required";
    }

    if (!formData.newPassword) {
      newErrors.newPassword =
        t.newPasswordRequired || "New password is required";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword =
        t.passwordTooShort || "Password must be at least 8 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword =
        t.confirmPasswordRequired || "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword =
        t.passwordsDoNotMatch || "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BaseUrlApi}/check-password`, {
        password: formData.currentPassword,
      });
      onSave({
        user_id: {
          password: formData.currentPassword,
        },
      });
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string") {
        toast(t.error, { description: msg, status: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[425px]"
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        <DialogHeader>
          <DialogTitle>{t.changePassword || "Change Password"}</DialogTitle>
          <DialogDescription>
            {t.changePasswordDesc ||
              "Enter your current password and a new password"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="currentPassword">
                {t.currentPassword || "Current Password"}
              </label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  name="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={formData.currentPassword}
                  onChange={handleChange}
                  className={errors.currentPassword ? "border-red-500" : ""}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                  <span className="sr-only">Toggle password visibility</span>
                </Button>
              </div>
              {errors.currentPassword && (
                <p className="text-red-500 text-sm">{errors.currentPassword}</p>
              )}
            </div>

            <div className="grid gap-2">
              <label htmlFor="newPassword">
                {t.newPassword || "New Password"}
              </label>
              <div className="relative">
                <Input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={formData.newPassword}
                  onChange={handleChange}
                  className={errors.newPassword ? "border-red-500" : ""}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {errors.newPassword && (
                <p className="text-red-500 text-sm">{errors.newPassword}</p>
              )}
            </div>

            <div className="grid gap-2">
              <label htmlFor="confirmPassword">
                {t.confirmPassword || "Confirm Password"}
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={errors.confirmPassword ? "border-red-500" : ""}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                  <span className="sr-only">Toggle password visibility</span>
                </Button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm">{errors.confirmPassword}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || loading}>
              {(saving || loading) && (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              )}
              {t.changePassword || "Change Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
